import React, { useState, useEffect } from "react";
import { FaPlus } from "react-icons/fa";
import Button from "./../../components/Button/Button";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import {
  getAllUsers,
  getLatestUsers,
  deleteUser as deleteUserAPI,
} from "./../../services/userServices";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import ManageAdherentsModal from "./AdherentManagementModal";
import { ToastContainer, toast } from "react-toastify";
import PageHeader from "./../../components/Layout/PageHeader";

const ROLE_META = {
  1: {
    name: "Administrateur",
    className:
      "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
  },
  2: {
    name: "Bureau",
    className:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  },
  3: {
    name: "Membres",
    className:
      "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  },
  4: {
    name: "Officiels",
    className:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  },
  5: {
    name: "Responsables clubs",
    className:
      "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200",
  },
  6: {
    name: "Utilisateurs",
    className:
      "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
  },
  7: {
    name: "Membre / arbitre / resp. club",
    className:
      "bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200",
  },
};

const getRoleDisplay = (roleId) => {
  return (
    ROLE_META[roleId] || {
      name: "Rôle inconnu",
      className:
        "bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200",
    }
  );
};

const UserListing = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isAdmin = userRole === "Administrateur" || userRole === "Bureau";

  const [users, setUsers] = useState([]);
  const [baseList, setBaseList] = useState([]); // liste de base (tous ou derniers)
  const [displayedUsers, setDisplayedUsers] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showManageAdherentsModal, setShowManageAdherentsModal] =
    useState(false);
  const [editUserId, setEditUserId] = useState(null);

  const applyFilters = (list, search, role) => {
    if (!Array.isArray(list)) return [];

    let filtered = [...list];

    if (search && search.trim()) {
      const lower = search.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          (u.nom && u.nom.toLowerCase().includes(lower)) ||
          (u.prenom && u.prenom.toLowerCase().includes(lower)) ||
          (u.email && u.email.toLowerCase().includes(lower))
      );
    }

    if (role) {
      filtered = filtered.filter(
        (u) => String(u.role_id) === String(role)
      );
    }

    return filtered;
  };

  const fetchData = async () => {
    try {
      const usersData = await getAllUsers();
      setUsers(usersData);
      setBaseList(usersData);
      setDisplayedUsers(applyFilters(usersData, searchTerm, roleFilter));
    } catch (error) {
      console.error("Erreur lors du chargement des données:", error);
      toast.error("Erreur lors du chargement des données");
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value || "";
    setSearchTerm(value);
    setDisplayedUsers(applyFilters(baseList, value, roleFilter));
  };

  const handleRoleFilterChange = (e) => {
    const value = e.target.value || "";
    setRoleFilter(value);
    setDisplayedUsers(applyFilters(baseList, searchTerm, value));
  };

  const deleteUser = async (userId) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer cet utilisateur ?`
      )
    ) {
      return;
    }
    try {
      const message = await deleteUserAPI(userId);
      toast.success(message || "Utilisateur supprimé avec succès");
      fetchData();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'utilisateur:", error);
      toast.error("Erreur lors de la suppression de l'utilisateur");
    }
  };

  const getRecentUsers = async () => {
    try {
      const recentUsers = await getLatestUsers();
      setBaseList(recentUsers);
      setDisplayedUsers(applyFilters(recentUsers, searchTerm, roleFilter));
    } catch (error) {
      toast.error("Erreur lors de la récupération des derniers utilisateurs");
    }
  };

  const getAllUsersAgain = () => {
    setBaseList(users);
    setDisplayedUsers(applyFilters(users, searchTerm, roleFilter));
  };

  const handleAddUser = () => {
    setShowModal(true);
  };

  const openEditModal = (userId) => {
    setEditUserId(userId);
    setShowEditModal(true);
  };

  const openManageAdherentsModal = (userId) => {
    setEditUserId(userId);
    setShowManageAdherentsModal(true);
  };

  const toAbsolutePhoto = (url) => {
    if (!url) return "/img/user_avatar.jpg";
    const s = String(url).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    if (!s.startsWith("/uploads")) return s;
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const totalUsers = displayedUsers.length;

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <ScrollIndicator />

      <div className="w-full max-w-6xl mx-auto px-2 sm:px-4 pb-10">
        <PageHeader
          eyebrow="Administrateur"
          title="Utilisateurs & accès"
          subtitle="Gérez les comptes, les rôles, et les liaisons avec les arbitres / acteurs du comité."
          align="center"
          size="md"
        />

        {/* Panneau d’actions & filtres */}
        <div className="mt-4 rounded-2xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/70 dark:border-slate-700/70 shadow-sm px-4 py-4 sm:px-6 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Actions gauche */}
            <div className="flex flex-wrap items-center gap-2">
              {isAdmin && (
                <Button
                  text="Ajouter un utilisateur"
                  onClick={handleAddUser}
                  icon={FaPlus}
                  className="!rounded-xl !px-4 !py-2 text-sm"
                />
              )}
              <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                Utilisateurs affichés :{" "}
                <span className="font-semibold text-primary">
                  {totalUsers}
                </span>
              </span>
            </div>

            {/* Filtres droite : rôle + recherche */}
            <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
              <select
                value={roleFilter}
                onChange={handleRoleFilterChange}
                className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs sm:text-sm px-3 py-2 text-slate-800 dark:text-slate-100"
              >
                <option value="">Tous rôles</option>
                {Object.entries(ROLE_META).map(([id, meta]) => (
                  <option key={id} value={id}>
                    {meta.name}
                  </option>
                ))}
              </select>

              <input
                className="input input-bordered w-full text-sm bg-white dark:bg-slate-800 dark:text-slate-50"
                type="text"
                placeholder="Rechercher (nom, prénom, email)…"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* Boutons “Derniers inscrits” / “Tous” */}
          <div className="flex flex-wrap gap-2">
            <Button
              text="Afficher les derniers inscrits"
              onClick={getRecentUsers}
              className="!rounded-xl !px-3 !py-1.5 text-xs sm:text-sm"
            />
            <Button
              text="Afficher tous"
              onClick={getAllUsersAgain}
              className="!rounded-xl !px-3 !py-1.5 text-xs sm:text-sm"
            />
          </div>
        </div>

        {/* Tableau utilisateurs */}
        <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200/70 dark:border-slate-700/70 bg-white/95 dark:bg-slate-900/90 shadow">
          <table className="min-w-full text-sm font-montserrat">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-[0.7rem] sm:text-xs font-semibold uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-4 py-3 text-left text-[0.7rem] sm:text-xs font-semibold uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-4 py-3 text-left text-[0.7rem] sm:text-xs font-semibold uppercase tracking-wider">
                  Acteurs liés
                </th>
                <th className="px-4 py-3 text-left text-[0.7rem] sm:text-xs font-semibold uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.map((u) => {
                const roleInfo = getRoleDisplay(u.role_id);
                return (
                  <tr
                    key={u.id}
                    className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/70"
                  >
                    {/* Utilisateur (photo + nom + email) */}
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-3">
                        <img
                          className="rounded-full h-11 w-11 object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          src={toAbsolutePhoto(u.photoURL)}
                          alt={`${u.prenom || ""} ${u.nom || ""}`}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {(u.nom || "").toUpperCase()} {u.prenom || ""}
                          </div>
                          {u.email && (
                            <div className="text-[0.7rem] text-slate-500 dark:text-slate-400 truncate">
                              {u.email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Rôle */}
                    <td className="px-4 py-3 align-top">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[0.7rem] font-semibold ${roleInfo.className}`}
                      >
                        {roleInfo.name}
                      </span>
                    </td>

                    {/* Acteurs liés */}
                    <td className="px-4 py-3 align-top text-xs sm:text-sm">
                      {u.acteurs && u.acteurs.length > 0 ? (
                        <div className="space-y-0.5">
                          {u.acteurs.map((acteur, idx) => (
                            <div
                              key={`${u.id}-${idx}`}
                              className="text-slate-700 dark:text-slate-200 text-[0.75rem] sm:text-xs"
                            >
                              {acteur.nom} {acteur.prenom}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[0.7rem] text-slate-400">
                          Aucun acteur associé
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-wrap gap-2 items-center">
                        <ButtonEdit
                          onClick={() => openEditModal(u.id)}
                          hoverLabel="Éditer"
                        />
                        <button
                          className="w-8 h-8 rounded-md bg-emerald-500 text-white text-xs font-bold shadow-sm transition hover:bg-emerald-600 flex items-center justify-center"
                          title="Gérer les acteurs liés"
                          onClick={() => openManageAdherentsModal(u.id)}
                        >
                          +/-
                        </button>
                        <ButtonDelete
                          onClick={() => deleteUser(u.id)}
                          hoverLabel="Supprimer"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}

              {displayedUsers.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-xs sm:text-sm text-slate-500 dark:text-slate-300"
                  >
                    Aucun utilisateur trouvé. Modifiez vos filtres ou
                    rechargez la liste.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modales */}
        <AddUserModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          refreshList={fetchData}
        />
        <EditUserModal
          isOpen={showEditModal}
          userId={editUserId}
          onClose={() => setShowEditModal(false)}
          refreshList={fetchData}
        />
        <ManageAdherentsModal
          isOpen={showManageAdherentsModal}
          onClose={() => setShowManageAdherentsModal(false)}
          userId={editUserId}
          refresh={fetchData}
        />

        <ToastContainer />
      </div>
    </div>
  );
};

export default UserListing;
